// Deployment trigger: 2026-04-24
import React, { useState, useMemo, useEffect } from 'react';
import { X, BarChart2, Loader2 } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format, subDays, subWeeks, subMonths, subYears, isAfter, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval, eachYearOfInterval, startOfDay, startOfWeek, startOfMonth, startOfYear } from 'date-fns';

type FilterType = 'Day' | 'Week' | 'Month' | 'Year';

interface UsageHistoryModalProps {
  projectId: string;
  onClose: () => void;
}

export const UsageHistoryModal: React.FC<UsageHistoryModalProps> = ({ projectId, onClose }) => {
  const [filter, setFilter] = useState<FilterType>('Month');
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<any[]>([]);

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const q = query(collection(db, 'leads'), where('projectId', '==', projectId));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => doc.data());
        setLeads(data);
      } catch (err) {
        console.error("Failed to fetch usage history:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [projectId]);

  const chartData = useMemo(() => {
    const now = new Date();
    let intervalDates: Date[] = [];
    let formatStr: string = '';

    switch (filter) {
      case 'Day':
        intervalDates = eachDayOfInterval({ start: subDays(now, 14), end: now });
        formatStr = 'MMM dd';
        break;
      case 'Week':
        intervalDates = eachWeekOfInterval({ start: subWeeks(now, 12), end: now });
        formatStr = 'MMM dd'; // Week of...
        break;
      case 'Month':
        intervalDates = eachMonthOfInterval({ start: subMonths(now, 11), end: now });
        formatStr = 'MMM yyyy';
        break;
      case 'Year':
        intervalDates = eachYearOfInterval({ start: subYears(now, 4), end: now });
        formatStr = 'yyyy';
        break;
    }

    const dataMap = new Map<string, { name: string; email: number; sms: number }>();

    intervalDates.forEach(date => {
      const key = format(date, formatStr);
      dataMap.set(key, { name: key, email: 0, sms: 0 });
    });

    leads.forEach(lead => {
      if (!lead.createdAt) return;
      const d = new Date(lead.createdAt);
      
      let key = '';
      switch (filter) {
        case 'Day': key = format(d, formatStr); break;
        case 'Week': key = format(startOfWeek(d), formatStr); break;
        case 'Month': key = format(startOfMonth(d), formatStr); break;
        case 'Year': key = format(startOfYear(d), formatStr); break;
      }

      if (dataMap.has(key)) {
        const entry = dataMap.get(key)!;
        if (lead.email) entry.email += 1;
        if (lead.phone) entry.sms += 1;
      }
    });

    return Array.from(dataMap.values());
  }, [leads, filter]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600">
              <BarChart2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Usage History</h2>
              <p className="text-sm text-slate-500 font-medium">Emails and SMS sent over time</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:bg-slate-100 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl">
              {(['Day', 'Week', 'Month', 'Year'] as FilterType[]).map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                    filter === f 
                      ? 'bg-white text-indigo-600 shadow-sm' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Emails</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">SMS</span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mb-4" />
              <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">Loading History...</p>
            </div>
          ) : (
            <div className="h-[400px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 12, fontWeight: 500 }}
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 12, fontWeight: 500 }}
                    dx={-10}
                  />
                  <Tooltip 
                    cursor={{ fill: '#f1f5f9' }}
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                  />
                  <Bar dataKey="email" name="Emails Sent" fill="#6366f1" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  <Bar dataKey="sms" name="SMS Sent" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
